import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { formatTime12Hour, formatDateDisplay } from '../../utils/timeFormat';
import BarberCalendar from './BarberCalendar';
import TeamChat from '../../components/TeamChat';
import { EngagementStatusBadge } from '../../components/EngagementStatusBadge';
import { 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  DollarSign, 
  TrendingUp, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Star,
  Scissors,
  Settings,
  CalendarOff,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BarberDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Schedule management state
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockedTimeSlots, setBlockedTimeSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [blockReason, setBlockReason] = useState('');

  const [activeTab, setActiveTab] = useState('today');
  const [unreadChatCount, setUnreadChatCount] = useState(0);

  // Poll for unread team-chat messages every 15s when not already on the chat tab
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'barber') return;
    let cancelled = false;

    const pull = async () => {
      try {
        const res = await api.get('/api/team-chat/unread-count');
        if (!cancelled) setUnreadChatCount(res.data?.count || 0);
      } catch (_) { /* silent */ }
    };
    pull();
    const id = setInterval(() => {
      if (activeTab !== 'chat' && !document.hidden) pull();
    }, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [currentUser, activeTab]);

  // Clear badge when the user opens the chat tab
  useEffect(() => {
    if (activeTab === 'chat') setUnreadChatCount(0);
  }, [activeTab]);

  useEffect(() => {
    // Redirect if not a barber
    if (currentUser && currentUser.role !== 'barber') {
      navigate('/');
      toast.error('Access denied. Barbers only.');
      return;
    }

    if (currentUser && currentUser.role === 'barber') {
      fetchDashboardData();
    }
  }, [currentUser, navigate]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, todayRes, upcomingRes, blockedRes] = await Promise.all([
        api.get('/api/barber/stats'),
        api.get('/api/barber/today'),
        api.get('/api/barber/upcoming?days=7'),
        api.get('/api/barber/blocked-times')
      ]);

      setStats(statsRes.data);
      setTodayAppointments(todayRes.data);
      setUpcomingAppointments(upcomingRes.data);
      setBlockedDates(blockedRes.data.blocked_dates || []);
      setBlockedTimeSlots(blockedRes.data.blocked_time_slots || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const blockFullDay = async () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    try {
      await api.post('/api/barber/block-date', { 
        date: selectedDate,
        reason: blockReason || 'Day off'
      });
      
      toast.success('Date blocked successfully!');
      setSelectedDate('');
      setBlockReason('');
      fetchDashboardData();
    } catch (error) {
      console.error('Error blocking date:', error);
      toast.error('Failed to block date');
    }
  };

  const blockTimeSlot = async () => {
    if (!selectedDate || !selectedTimeSlot) {
      toast.error('Please select both date and time');
      return;
    }

    try {
      await api.post('/api/barber/block-time-slot', { 
        date: selectedDate,
        time: selectedTimeSlot,
        reason: blockReason || 'Unavailable'
      });
      
      toast.success('Time slot blocked successfully!');
      setSelectedDate('');
      setSelectedTimeSlot('');
      setBlockReason('');
      fetchDashboardData();
    } catch (error) {
      console.error('Error blocking time slot:', error);
      toast.error('Failed to block time slot');
    }
  };

  const unblockDate = async (dateId) => {
    try {
      await api.delete(`/api/barber/blocked-dates/${dateId}`);
      toast.success('Date unblocked!');
      fetchDashboardData();
    } catch (error) {
      console.error('Error unblocking date:', error);
      toast.error('Failed to unblock date');
    }
  };

  const unblockTimeSlot = async (slotId) => {
    try {
      await api.delete(`/api/barber/blocked-time-slots/${slotId}`);
      toast.success('Time slot unblocked!');
      fetchDashboardData();
    } catch (error) {
      console.error('Error unblocking time slot:', error);
      toast.error('Failed to unblock time slot');
    }
  };

  // Generate time slots (8 AM – 7:30 PM — covers standard hours 9-5:30 PLUS
  // Members-only early/late slots 8:00, 8:30, 18:00, 18:30, 19:00, 19:30 so
  // barbers can see + manage the member-priority hours).
  const timeSlots = [];
  for (let hour = 8; hour <= 19; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
  }

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await api.patch(`/api/barber/appointments/${appointmentId}/status`, { status });
      
      toast.success(`Appointment marked as ${status}!`);
      
      // Refresh data
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Failed to update appointment');
    }
  };

  const chargeNoShowFee = async (appointmentId, customerName) => {
    const ok = window.confirm(
      `Charge $20 no-show fee to ${customerName || 'this customer'}'s card on file?\n\nThis will run the Stripe charge immediately. If the card is missing or declines, the customer will be flagged for manual follow-up.`
    );
    if (!ok) return;
    try {
      const res = await api.post(`/api/barber/appointments/${appointmentId}/charge-no-show`);
      const d = res.data || {};
      if (d.already_recorded) {
        toast.info('No-show fee was already processed for this appointment.');
      } else if (d.charged) {
        toast.success(`$20 no-show fee charged successfully`);
      } else {
        toast.error(`Charge failed: ${d.reason || 'unknown error'}`);
      }
      fetchDashboardData();
    } catch (error) {
      console.error('Error charging no-show fee:', error);
      toast.error(error.response?.data?.detail || 'Failed to charge no-show fee');
    }
  };

  const AppointmentCard = ({ appointment }) => {
    // Support both new format (separate date+time) and legacy (start_time ISO string)
    const parsedDateTime = appointment.start_time
      ? new Date(appointment.start_time)
      : appointment.date && appointment.time
        ? new Date(`${appointment.date}T${appointment.time}:00`)
        : new Date(appointment.date);
    const appointmentDate = parsedDateTime;
    const isToday = appointmentDate.toDateString() === new Date().toDateString();

    const displayDate = appointment.date
      ? formatDateDisplay(appointment.date)
      : appointmentDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
    const displayTime = appointment.time
      ? formatTime12Hour(appointment.time)
      : appointmentDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });

    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1 space-y-3">
              {/* Customer Info */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">{appointment.customer_name}</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    appointment.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                    appointment.status === 'arrived' ? 'bg-lime-500/20 text-lime-400' :
                    appointment.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                    appointment.status === 'no-show' ? 'bg-red-500/20 text-red-400' :
                    'bg-zinc-500/20 text-zinc-400'
                  }`}>
                    {appointment.status.toUpperCase()}
                  </span>
                  {isToday && (
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400">
                      TODAY
                    </span>
                  )}
                </div>
              </div>

              {/* Appointment Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center text-zinc-300">
                  <Calendar className="w-4 h-4 mr-2 text-purple-400" />
                  {displayDate}
                </div>
                <div className="flex items-center text-zinc-300">
                  <Clock className="w-4 h-4 mr-2 text-purple-400" />
                  {displayTime}
                </div>
                <div className="flex items-center text-zinc-300">
                  <Scissors className="w-4 h-4 mr-2 text-purple-400" />
                  {appointment.service_name}
                </div>
                <div className="flex items-center text-lime-400 font-semibold">
                  <DollarSign className="w-4 h-4 mr-1" />
                  ${appointment.service_price}
                </div>
              </div>

              {/* Customer Phone */}
              {appointment.customer_phone && (
                <div className="flex items-center text-zinc-300 text-sm">
                  <Phone className="w-4 h-4 mr-2 text-purple-400" />
                  <a href={`tel:${appointment.customer_phone}`} className="hover:text-lime-400">
                    {appointment.customer_phone}
                  </a>
                </div>
              )}

              {/* Notes */}
              {appointment.notes && (
                <div className="text-sm bg-zinc-800 p-3 rounded text-zinc-300">
                  <strong className="text-white">Notes:</strong> {appointment.notes}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
              <div className="flex flex-col gap-2 md:w-48">
                {appointment.status === 'confirmed' && (
                  <Button
                    onClick={() => updateAppointmentStatus(appointment.id, 'arrived')}
                    className="bg-lime-500 text-black hover:bg-lime-600 w-full"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Customer Arrived
                  </Button>
                )}
                
                {appointment.status === 'arrived' && (
                  <Button
                    onClick={() => updateAppointmentStatus(appointment.id, 'completed')}
                    className="bg-green-500 text-white hover:bg-green-600 w-full"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark Complete
                  </Button>
                )}
                
                {appointment.status !== 'no-show' && (
                  <Button
                    onClick={() => updateAppointmentStatus(appointment.id, 'no-show')}
                    variant="outline"
                    className="border-red-500 text-red-400 hover:bg-red-500/10 w-full"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Mark No-Show
                  </Button>
                )}

                {appointment.status === 'no-show' && !appointment.no_show_fee_charged && (
                  <Button
                    onClick={() => chargeNoShowFee(appointment.id, appointment.customer_name)}
                    className="bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700 w-full"
                    data-testid={`charge-no-show-btn-${appointment.id}`}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Charge $20 Fee
                  </Button>
                )}

                {appointment.status === 'no-show' && appointment.no_show_fee_charged && (
                  <div className="text-xs text-center text-lime-400 bg-lime-500/10 border border-lime-500/30 rounded py-2 px-2">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    $20 Fee Collected
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-lime-400 border-t-transparent mx-auto mb-4"></div>
          <p className="text-zinc-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-2">
            Welcome, <span className="text-lime-400">{stats?.barber_name}</span>
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-zinc-400 text-lg">Manage your appointments and schedule</p>
            {stats?.engagement_status && (
              <EngagementStatusBadge
                status={stats.engagement_status}
                size="lg"
                showHint
                testId="barber-self-engagement-badge"
              />
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Today</p>
                  <p className="text-3xl font-bold text-white">{stats?.today_appointments || 0}</p>
                </div>
                <Calendar className="w-12 h-12 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-lime-900/50 to-lime-800/30 border-lime-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm mb-1">This Week</p>
                  <p className="text-3xl font-bold text-white">{stats?.week_appointments || 0}</p>
                </div>
                <TrendingUp className="w-12 h-12 text-lime-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Month Revenue</p>
                  <p className="text-3xl font-bold text-white">${stats?.month_revenue || 0}</p>
                </div>
                <DollarSign className="w-12 h-12 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-900/50 to-yellow-800/30 border-yellow-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Rating</p>
                  <p className="text-3xl font-bold text-white">{stats?.rating || 5.0} ⭐</p>
                </div>
                <Star className="w-12 h-12 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Appointments Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6 grid grid-cols-2 sm:grid-cols-5 gap-1 h-auto p-1 w-full">
            <TabsTrigger 
              value="today" 
              className="data-[state=active]:bg-lime-500 data-[state=active]:text-black text-xs sm:text-sm"
            >
              Today ({todayAppointments.length})
            </TabsTrigger>
            <TabsTrigger 
              value="upcoming"
              className="data-[state=active]:bg-purple-500 text-xs sm:text-sm"
            >
              Upcoming ({upcomingAppointments.length})
            </TabsTrigger>
            <TabsTrigger 
              value="calendar"
              className="data-[state=active]:bg-blue-500 text-xs sm:text-sm"
            >
              📅 Calendar
            </TabsTrigger>
            <TabsTrigger 
              value="schedule"
              className="data-[state=active]:bg-orange-500 text-xs sm:text-sm"
              data-testid="manage-schedule-tab"
            >
              <Settings className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Manage Schedule</span>
              <span className="sm:hidden">Schedule</span>
            </TabsTrigger>
            <TabsTrigger 
              value="chat"
              className="data-[state=active]:bg-pink-500 text-xs sm:text-sm relative"
              data-testid="team-chat-tab"
            >
              <MessageCircle className="w-4 h-4 mr-1 sm:mr-2" />
              <span>Team Chat</span>
              {unreadChatCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center"
                  data-testid="team-chat-unread-badge"
                >
                  {unreadChatCount > 9 ? '9+' : unreadChatCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Today's Appointments */}
          <TabsContent value="today" className="space-y-4">
            {todayAppointments.length > 0 ? (
              todayAppointments.map(apt => (
                <AppointmentCard key={apt.id} appointment={apt} />
              ))
            ) : (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                  <h3 className="text-xl font-semibold mb-2">No Appointments Today</h3>
                  <p className="text-zinc-400">Enjoy your day off! 🎉</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Upcoming Appointments */}
          <TabsContent value="upcoming" className="space-y-4">
            {upcomingAppointments.length > 0 ? (
              upcomingAppointments.map(apt => (
                <AppointmentCard key={apt.id} appointment={apt} />
              ))
            ) : (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-12 text-center">
                  <AlertCircle className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                  <h3 className="text-xl font-semibold mb-2">No Upcoming Appointments</h3>
                  <p className="text-zinc-400">Check back later for new bookings</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Calendar View */}
          <TabsContent value="calendar">
            <BarberCalendar />
          </TabsContent>

          {/* Schedule Management */}
          <TabsContent value="schedule" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Block Full Day */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <CalendarOff className="w-6 h-6 text-orange-400" />
                    <h3 className="text-xl font-semibold">Block Full Day</h3>
                  </div>
                  <p className="text-zinc-400 mb-4">Block an entire day (no appointments will be accepted)</p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Select Date</label>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Reason (Optional)</label>
                      <input
                        type="text"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        placeholder="e.g., Vacation, Sick day"
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                      />
                    </div>
                    
                    <Button 
                      onClick={blockFullDay}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <CalendarOff className="w-4 h-4 mr-2" />
                      Block This Day
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Block Time Slot */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-6 h-6 text-purple-400" />
                    <h3 className="text-xl font-semibold">Block Time Slot</h3>
                  </div>
                  <p className="text-zinc-400 mb-4">Block specific time slot on a date</p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Select Date</label>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Select Time</label>
                      <select
                        value={selectedTimeSlot}
                        onChange={(e) => setSelectedTimeSlot(e.target.value)}
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                      >
                        <option value="">Choose a time</option>
                        {timeSlots.map(slot => {
                          // Member-priority hours: 8:00, 8:30 (early) + 18:00, 18:30, 19:00, 19:30 (late)
                          const [h] = slot.split(':').map(Number);
                          const isMemberOnly = h === 8 || h >= 18;
                          return (
                            <option key={slot} value={slot} data-testid={`slot-${slot}`}>
                              {formatTime12Hour(slot)}{isMemberOnly ? '  👑 Members' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Reason (Optional)</label>
                      <input
                        type="text"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        placeholder="e.g., Lunch break, Meeting"
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                      />
                    </div>
                    
                    <Button 
                      onClick={blockTimeSlot}
                      className="w-full bg-purple-500 hover:bg-purple-600 text-white"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Block This Time
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Blocked Dates List */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">Blocked Full Days</h3>
                {blockedDates.length > 0 ? (
                  <div className="space-y-2">
                    {blockedDates.map((blocked) => (
                      <div key={blocked.id} className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                        <div>
                          <p className="font-medium">{new Date(blocked.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                          {blocked.reason && <p className="text-sm text-zinc-400">{blocked.reason}</p>}
                        </div>
                        <Button
                          onClick={() => unblockDate(blocked.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
                        >
                          Unblock
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-400 text-center py-8">No blocked days</p>
                )}
              </CardContent>
            </Card>

            {/* Blocked Time Slots List */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">Blocked Time Slots</h3>
                {blockedTimeSlots.length > 0 ? (
                  <div className="space-y-2">
                    {blockedTimeSlots.map((blocked) => (
                      <div key={blocked.id} className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                        <div>
                          <p className="font-medium">{new Date(blocked.date).toLocaleDateString()} at {formatTime12Hour(blocked.time)}</p>
                          {blocked.reason && <p className="text-sm text-zinc-400">{blocked.reason}</p>}
                        </div>
                        <Button
                          onClick={() => unblockTimeSlot(blocked.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
                        >
                          Unblock
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-400 text-center py-8">No blocked time slots</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Chat */}
          <TabsContent value="chat" className="space-y-4">
            <TeamChat height="70vh" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BarberDashboard;
